var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineSelect, Text } from '@ui';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
import { FILE_ACCESS_VALUES, fileAccessLabel } from '../utils/fileAccess';
/**
 * Inline-editable `Visibility` cell — the one editable field this surface has besides the name.
 *
 * `InlineSelect` is genuinely controlled (unlike `InlineText`'s contentEditable): its displayed
 * `value` never changes until the parent re-renders with a new one, so — per the design's own
 * "don't optimistically update" note — `value` here is only ever the last server-confirmed access.
 * A failed commit therefore reverts for free: the displayed value never moved, so only the inline
 * error needs surfacing.
 */
export function AttachmentFileAccessCell({ item, invoiceId, translateFn, readOnly }) {
    const { commit } = useAttachmentFieldCommit(invoiceId, item.id);
    const [fileAccess, setFileAccess] = useState(item.fileAccess);
    const [error, setError] = useState(null);
    // Refetch-clobber guard (InspectionStatusRenderer.tsx:52-55) — a listing refetch replaces `item`
    // without remounting this cell, so skip re-syncing while a commit is in flight. Read via a ref,
    // not a state dependency: React 17 does not batch state updates made from a Promise callback, so
    // depending on an `isSaving` state value would re-run this effect the moment the commit cleared
    // it and reset `fileAccess` from the still-stale `item` prop.
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (isSavingRef.current)
            return;
        setFileAccess(item.fileAccess);
    }, [item.fileAccess]);
    if (readOnly) {
        return React.createElement(Text, { variant: "body" }, fileAccessLabel(fileAccess, translateFn));
    }
    const handleChange = (nextFileAccess) => __awaiter(this, void 0, void 0, function* () {
        if (nextFileAccess === fileAccess)
            return;
        isSavingRef.current = true;
        setError(null);
        const result = yield commit({ fileAccess: nextFileAccess });
        isSavingRef.current = false;
        if (result.success) {
            setFileAccess(result.row.fileAccess);
            setError(null);
        }
        else {
            setError(result.error);
        }
    });
    return (React.createElement("span", { className: "attachments-surface__file-access-cell", "data-action-cell": true },
        React.createElement(InlineSelect, { options: FILE_ACCESS_VALUES.map((value) => ({ value, label: fileAccessLabel(value, translateFn) })), value: fileAccess, onChange: (value) => {
                void handleChange(value);
            } }),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error" }, error))));
}
